/**
 * TRUS-732: the member's case manager per program, as each program's Case
 * assignment setup says — its label (Case Manager, Care Coordinator, DCM…), its
 * field of record, and its list of people (staff ticked for the program, still
 * in the org, active).
 *
 *  - A case manager who has left keeps showing by name (the user record outlives
 *    the org membership), so closed cases keep their history.
 *  - An open case whose case manager is no longer on the list shows
 *    "Needs reassignment", when the program has that flag switched on.
 *  - The dropdown offers only the current list; a departed case manager stays
 *    visible as the current choice but cannot be picked again.
 *
 * Renders nothing for a member with no set-up program, so every other org keeps
 * the profile it has today.
 */
import React, { useContext, useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2, Pencil, UserCheck, X } from 'lucide-react';
import TranslationContext from '@/contexts/TranslationContext';
import {
  caseAssignmentAPI,
  type CaseManagerOption,
  type MemberCaseAssignment,
} from '@/services/api/caseAssignment';

interface Props {
  memberId: number;
  /** Called once loaded: true when a program stores its case manager on the
   *  member's own Case Manager field, so the generic selector would duplicate it. */
  onLoaded?: (usesMemberCaseManager: boolean) => void;
  /** View-only users see the case manager and the flag, but cannot change it. */
  readOnly?: boolean;
}

export default function ProgramCaseAssignments({ memberId, onLoaded, readOnly = false }: Props) {
  // Optional context, as in InlineCustomFieldEditor: tests render without a provider.
  const i18n = useContext(TranslationContext);
  const t = (key: string, fallback: string, opts?: Record<string, unknown>): string =>
    i18n ? i18n.t(key, fallback, opts) : fallback.replace(/\{\{(\w+)\}\}/g, (_m, k) => String(opts?.[k] ?? ''));

  const [rows, setRows] = useState<MemberCaseAssignment[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  const [options, setOptions] = useState<CaseManagerOption[]>([]);
  const [choice, setChoice] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await caseAssignmentAPI.getMemberAssignments(memberId);
      setRows(data);
      onLoaded?.(data.some((r) => r.assignee_field === 'member_case_manager'));
    } catch {
      setRows([]);
      onLoaded?.(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const startEdit = async (row: MemberCaseAssignment) => {
    setError(null);
    setEditing(row.program_id);
    setChoice(row.assignee_user_id != null ? String(row.assignee_user_id) : '');
    try {
      setOptions(await caseAssignmentAPI.listCaseManagers(row.program_id));
    } catch {
      setOptions([]);
    }
  };

  const save = async (row: MemberCaseAssignment) => {
    const next = choice === '' ? null : Number(choice);
    if (next === row.assignee_user_id) {
      setEditing(null);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await caseAssignmentAPI.assign(memberId, row.program_id, next);
      setEditing(null);
      await load();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('caseAssignment.assignFailed', 'Could not save the assignment.'));
    } finally {
      setBusy(false);
    }
  };

  if (rows.length === 0) return null;
  const many = rows.length > 1;

  return (
    <div className="space-y-2" data-testid="program-case-assignments">
      {rows.map((row) => {
        const heading = many
          ? t('caseAssignment.labelForProgram', '{{label}} ({{program}})', { label: row.label, program: row.program_name })
          : row.label;
        const offList = row.assignee_user_id != null && !row.assignee_on_list;
        return (
          <div key={row.program_id} className="flex flex-wrap items-center gap-2 text-sm">
            <UserCheck className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{heading}</span>

            {editing === row.program_id ? (
              <>
                <select
                  aria-label={heading}
                  value={choice}
                  onChange={(e) => setChoice(e.target.value)}
                  autoFocus
                  className="h-8 text-sm border border-blue-300 rounded px-2 bg-white dark:bg-gray-900"
                >
                  <option value="">{t('caseAssignment.unassigned', '— Unassigned —')}</option>
                  {offList && (
                    <option value={String(row.assignee_user_id)} disabled>
                      {t('caseAssignment.offList', '{{name}} (no longer on the list)', { name: row.assignee_name ?? '' })}
                    </option>
                  )}
                  {options.map((o) => (
                    <option key={o.user_id} value={String(o.user_id)}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => save(row)}
                  disabled={busy}
                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                  title={t('caseAssignment.saveAssignment', 'Save')}
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="p-1 text-gray-400 hover:bg-gray-50 rounded"
                  title={t('caseAssignment.cancel', 'Cancel')}
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : readOnly ? (
              <span className="font-medium">
                {row.assignee_name ?? (
                  <span className="text-gray-400 italic font-normal">{t('caseAssignment.notAssigned', 'Not assigned')}</span>
                )}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => startEdit(row)}
                className="group inline-flex items-center gap-1 font-medium"
                title={t('caseAssignment.change', 'Change')}
              >
                {row.assignee_name ?? (
                  <span className="text-gray-400 italic font-normal">{t('caseAssignment.notAssigned', 'Not assigned')}</span>
                )}
                <Pencil className="w-3 h-3 text-gray-300 group-hover:text-blue-500" />
              </button>
            )}

            {row.needs_reassignment && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 whitespace-nowrap"
                title={t(
                  'caseAssignment.needsReassignmentHint',
                  'This open case is assigned to someone who is no longer on the case manager list. Choose a current one.',
                )}
              >
                <AlertTriangle className="w-3 h-3" />
                {t('members.needsReassignment', 'Needs reassignment')}
              </span>
            )}
          </div>
        );
      })}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
